'use client';

import { useState } from 'react';
import { flushSync } from 'react-dom';
import { TextField, Divider, Checkbox, Loader } from 'ui';
import { useTranslation } from '@/i18n/client';
import { useProductList } from '@/modules/product/context/productList';
import { useCurrentLocale } from '@/shared/utils/client';
import { fetchBrandsFilterList } from '@/modules/brands/api';
import { fetchFilterList } from '@/modules/product/api/productList';

import { formatFiltersBeforeSend } from '../filterBar/utils';
import { CheckboxFilterProps } from './types';

export const CheckboxFilter = ({
	initialValues,
	filteredValues,
	selectedValues,
	sidebarFilters,
	slug,
	unitName,
	setSidebarFilters,
	setSidebarFilteredFilterList,
}: CheckboxFilterProps) => {
	const { t } = useTranslation(['common']);
	const locale = useCurrentLocale();
	const { categorySlug, brandSlug } = useProductList();
	const [search, setSearch] = useState('');
	const [values, setValues] = useState(initialValues);
	const [isLoading, setIsLoading] = useState(false);

	const handleSearchChange = (value: string) => {
		setValues(
			initialValues.filter((v) =>
				v.toLocaleLowerCase(locale).trim().includes(value.toLocaleLowerCase(locale).trim())
			)
		);
		setSearch(value);
	};

	const handleSelectValue = async (selected: boolean, name: string) => {
		const newSidebarFiltersValue = [...selectedValues] as string[];

		if (!selected) {
			if (newSidebarFiltersValue.indexOf(name) > -1) {
				return;
			}

			newSidebarFiltersValue.push(name);
		} else {
			const valueIndex = newSidebarFiltersValue.indexOf(name);

			if (newSidebarFiltersValue.indexOf(name) < 0) {
				return;
			}

			newSidebarFiltersValue.splice(valueIndex, 1);
		}

		flushSync(() => {
			setSidebarFilters((oldValue) => {
				const newValue = [...oldValue];

				const filterIndex = newValue.findIndex((filter) => filter.slug === slug);

				newValue[filterIndex].selectedValue = newSidebarFiltersValue;

				return newValue;
			});
		});
		setIsLoading(true);

		const newFilters = formatFiltersBeforeSend([...sidebarFilters]);

		if (newSidebarFiltersValue.length > 0) {
			newFilters[slug] =
				unitName === null
					? newSidebarFiltersValue
					: newSidebarFiltersValue.map((value) => value.replace(` ${unitName}`, ''));
		} else {
			delete newFilters[slug];
		}

		const response = brandSlug
			? await fetchBrandsFilterList(brandSlug, newFilters)
			: await fetchFilterList(categorySlug, newFilters);

		setSidebarFilteredFilterList(response);
		setIsLoading(false);
	};

	return (
		<>
			{initialValues.length >= 10 && (
				<>
					<TextField
						label={t('common.search')}
						value={search}
						onChange={(e) => handleSearchChange(e.target.value)}
					/>
					<Divider disableBackground bottom={15} />
				</>
			)}
			<ul>
				{values.sort().map((value, index) => {
					const disabled = !filteredValues.includes(value);

					if (disabled) {
						return;
					}

					if (value === '0,00' || value === 'N/A') {
						return null;
					}

					return (
						<li key={value}>
							<Checkbox
								key={index}
								onChange={() =>
									handleSelectValue(
										selectedValues.findIndex(
											(v) => v === `${value}${unitName !== null ? ` ${unitName}` : ''}`
										) > -1,
										`${value}${unitName !== null ? ` ${unitName}` : ''}`
									)
								}
								checked={
									selectedValues.findIndex(
										(v) => v === `${value}${unitName !== null ? ` ${unitName}` : ''}`
									) > -1
								}
								disabled={disabled}
								position="start"
							>
								{`${value}${unitName !== null ? ` ${unitName}` : ''}`}
							</Checkbox>
						</li>
					);
				})}
				{values.sort().map((value, index) => {
					const disabled = !filteredValues.includes(value);

					if (!disabled) {
						return;
					}

					return (
						<li key={value}>
							<Checkbox
								key={index}
								onChange={() =>
									handleSelectValue(
										selectedValues.findIndex(
											(v) => v === `${value}${unitName !== null ? ` ${unitName}` : ''}`
										) > -1,
										`${value}${unitName !== null ? ` ${unitName}` : ''}`
									)
								}
								checked={
									selectedValues.findIndex(
										(v) => v === `${value}${unitName !== null ? ` ${unitName}` : ''}`
									) > -1
								}
								disabled={disabled}
								position="start"
							>
								{`${value}${unitName !== null ? ` ${unitName}` : ''}`}
							</Checkbox>
						</li>
					);
				})}
			</ul>
			{isLoading && <Loader />}
		</>
	);
};
